import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDate } from "./formatDate";

const date = new Date();

export const exportPdf = {
  table: (data) => {
    // Use landscape orientation for wider tables
    let doc = new jsPDF("portrait", "pt", "a4");
    let y = 30;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);

    // Calculate available width
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const availableWidth = pageWidth - 60;
    const currDate = formatDate.long(date.getTime() / 1000);

    if (!data.title) {
      data.title = " ";
    }

    const header = `${data.title} | Insights Export | Date Exported: ${currDate}`;
    const headerBlock = Math.ceil(
      (doc.splitTextToSize(header, 750).length * doc.getLineHeight(header)) /
        doc.internal.scaleFactor
    );

    doc.text(doc.splitTextToSize(header, 750), 30, y);
    y += headerBlock + 20;
    // Add order details if provided
    if (data.orderDetails) {
      const orderDetailsTitle = "Order Details:";
      const BusinessName = `Business Name: ${data.businessName || ""}`;
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(BusinessName, 30, y);
      y += 20;
      doc.setFont("helvetica", "bold");
      doc.text(orderDetailsTitle, 30, y);
      y += 20;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const orderDetailsBlock = Math.ceil(
        (doc.splitTextToSize(data.orderDetails, availableWidth).length *
          doc.getLineHeight(data.orderDetails)) /
          doc.internal.scaleFactor
      );
      doc.text(doc.splitTextToSize(data.orderDetails, availableWidth), 30, y);
      y += orderDetailsBlock + 30;
    }

    if (data.filter) {
      const filter = `Filtered By: \n\nBrands: ${data.filter.brands?.join(
        ", "
      )} \n\nChains: ${data.filter.chains?.join(
        ", "
      )} \n\nRevenue Center: ${data.filter.revCent?.join(
        ", "
      )}  \n\nPayment Type: ${data.filter.payType?.join(
        ", "
      )} \n\nDate: ${formatDate.longShort(
        data.filter.date.start
      )} to: ${formatDate.longShort(data.filter.date.end)}`;

      const filterBlock = Math.ceil(
        (doc.splitTextToSize(filter, availableWidth).length *
          doc.getLineHeight(filter)) /
          doc.internal.scaleFactor
      );

      doc.text(doc.splitTextToSize(filter, availableWidth), 30, y);
      y += filterBlock + 20;
    }

    data.body.forEach((body, key) => {
      // Define column styles for better formatting
      const columnStyles = {
        0: { cellWidth: 80 }, // Item No
        1: { cellWidth: 80 }, // Name
        2: { cellWidth: 40 }, // Qty
        3: { cellWidth: 40 }, // Price
        4: { cellWidth: 40 }, // Discount
        5: { cellWidth: 40 }, // Sales
        6: {
          cellWidth: 40, // Total Sales Per Outlet - wider
          fontSize: 8, // Smaller font for this column
          cellPadding: 3,
        },
        7: {
          cellWidth: 80, // Total Returns Per Outlet - wider
          fontSize: 8, // Smaller font for this column
          cellPadding: 3,
        },
        8: {
          cellWidth: 80, // Net Sales Per Outlet - wider
          fontSize: 8, // Smaller font for this column
          cellPadding: 3,
        },
      };

      autoTable(doc, {
        startY: y,
        theme: "striped",
        ...(key > 0 && { pageBreak: "always" }),
        margin: { top: 30, right: 30, bottom: 30, left: 30 },
        headStyles: {
          fillColor: "#212529",
          textColor: "#ffffff",
          lineColor: "#524e4e",
          fontSize: 9,
          cellPadding: 4,
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3,
          valign: "top", // Align text to top for multiline content
        },
        // columnStyles: columnStyles,
        head: body.head,
        body: body.body,
        // Handle text wrapping for long content
        // didParseCell: function (data) {
        //   if (data.column.index === 6) {
        //     // Total Sales Per Outlet column
        //     // data.cell.styles.cellWidth = 200;
        //     data.cell.styles.overflow = "linebreak";
        //     data.cell.styles.fontSize = 8;
        //   }
        // },
      });

      // Update y position for next table
      y = doc.lastAutoTable.finalY + 20;
    });

    doc.save(
      "insightsExport_" +
        data.title.split(" ").join("_") +
        "_Report_" +
        date.getTime() +
        ".pdf"
    );
  },

  image: (data) => {
    const container = document.getElementById(data.body.id);
    const currDate = formatDate.long(date.getTime() / 1000);

    // Force container to show full content before capturing
    const originalStyle = {
      height: container.style.height,
      overflow: container.style.overflow,
      maxHeight: container.style.maxHeight,
    };

    // Temporarily expand container to full size
    container.style.height = "auto";
    container.style.overflow = "visible";
    container.style.maxHeight = "auto";

    // Also expand any bar chart containers
    const barContainers = container.querySelectorAll(
      ".bar-chart-container, .sales-summary-chart-container"
    );
    const barOriginalStyles = [];
    barContainers.forEach((barContainer, index) => {
      barOriginalStyles[index] = {
        height: barContainer.style.height,
        overflow: barContainer.style.overflow,
        maxHeight: barContainer.style.maxHeight,
      };
      barContainer.style.height = "auto";
      barContainer.style.overflow = "visible";
      barContainer.style.maxHeight = "auto";
    });

    // Wait a moment for the DOM to update
    return new Promise((resolve) => {
      setTimeout(() => {
        html2canvas(container, {
          scale: 1.5, // Slightly lower scale for better performance with large charts
          useCORS: true,
          allowTaint: true,
          height: container.scrollHeight + 500, // Use scroll height to capture full content
          width: container.scrollWidth,
          scrollX: 0,
          scrollY: 0,
          windowWidth: container.scrollWidth,
          windowHeight: container.scrollHeight + 500,
        }).then((canvas) => {
          // Restore original styles
          container.style.height = originalStyle.height + 500;
          container.style.overflow = originalStyle.overflow;
          container.style.maxHeight = originalStyle.maxHeight + 500;

          barContainers.forEach((barContainer, index) => {
            if (barOriginalStyles[index]) {
              barContainer.style.height = barOriginalStyles[index].height + 500;
              barContainer.style.overflow = barOriginalStyles[index].overflow;
              barContainer.style.maxHeight =
                barOriginalStyles[index].maxHeight + 500;
            }
          });

          const imgData = canvas.toDataURL("image/png");

          // Create PDF with proper sizing for large images
          let doc = new jsPDF("portrait", "pt", "a4");
          let y = 30;
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);

          if (!data.title) {
            data.title = " ";
          }

          // Calculate available width
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight() + 200;
          const availableWidth = pageWidth - 60;
          const availableHeight = pageHeight - 120; // Leave space for header and footer

          const header =
            data.title + " | Insights Export | Date Exported: " + currDate;
          const headerBlock = Math.ceil(
            (doc.splitTextToSize(header, availableWidth).length *
              doc.getLineHeight(header)) /
              doc.internal.scaleFactor
          );

          doc.text(doc.splitTextToSize(header, availableWidth), 30, y);
          y += headerBlock + 20;

          if (data.filter) {
            const filter = `Filtered By: \n\nBrands: ${data.filter.brands?.join(
              ", "
            )} \n\nChains: ${data.filter.chains?.join(
              ", "
            )} \n\nRevenue Center: ${data.filter.revCent?.join(
              ", "
            )}  \n\nPayment Type: ${data.filter.payType?.join(
              ", "
            )} \n\nDate: ${formatDate.longShort(
              data.filter.date.start
            )} to: ${formatDate.longShort(data.filter.date.end)}`;

            const filterBlock = Math.ceil(
              (doc.splitTextToSize(filter, availableWidth).length *
                doc.getLineHeight(filter)) /
                doc.internal.scaleFactor
            );

            doc.text(doc.splitTextToSize(filter, availableWidth), 30, y);
            y += filterBlock + 20;
          }

          // Calculate image dimensions
          const maxWidth = availableWidth;
          const maxHeight = availableHeight - y;

          let imgWidth = canvas.width;
          let imgHeight = canvas.height;

          // Scale image to fit page width
          if (imgWidth > maxWidth) {
            const scale = maxWidth / imgWidth;
            imgWidth = maxWidth;
            imgHeight = imgHeight * scale;
          }

          // If image is still too tall, split across multiple pages
          if (imgHeight > maxHeight) {
            const pages = Math.ceil(imgHeight / maxHeight);
            const pageHeight = imgHeight / pages;

            for (let page = 0; page < pages; page++) {
              if (page > 0) {
                doc.addPage();
                y = 30;
              }

              // Create a cropped canvas for this page
              const croppedCanvas = document.createElement("canvas");
              const ctx = croppedCanvas.getContext("2d");
              croppedCanvas.width = canvas.width;
              croppedCanvas.height = (canvas.height + 500) / pages;

              ctx.drawImage(
                canvas,
                0,
                ((canvas.height + 500) / pages) * page,
                canvas.width,
                (canvas.height + 500) / pages,
                0,
                0,
                canvas.width,
                (canvas.height + 500) / pages
              );

              const croppedImgData = croppedCanvas.toDataURL("image/png");
              doc.addImage(croppedImgData, "PNG", 30, y, imgWidth, pageHeight);
            }
          } else {
            // Image fits on one page
            doc.addImage(imgData, "PNG", 30, y, imgWidth, imgHeight);
          }

          y += imgHeight + 20;

          // Add tables if they exist
          data.body.table?.forEach((body, key) => {
            const columnStyles = {
              0: { cellWidth: 50 },
              1: { cellWidth: 80 },
              2: { cellWidth: 40 },
              3: { cellWidth: 80 },
              4: { cellWidth: 70 },
              5: { cellWidth: 80 },
              6: { cellWidth: 100, fontSize: 8, cellPadding: 3 },
              7: { cellWidth: 100, fontSize: 8, cellPadding: 3 },
            };

            autoTable(doc, {
              startY: y,
              theme: "striped",
              margin: { top: 30, right: 30, bottom: 30, left: 30 },
              headStyles: {
                fillColor: "#212529",
                textColor: "#ffffff",
                lineColor: "#524e4e",
                fontSize: 9,
              },
              // columnStyles: columnStyles,
              bodyStyles: {
                fontSize: 8,
                cellPadding: 3,
                valign: "top",
              },
              head: body.head,
              body: body.body,
              didParseCell: function (data) {
                // if (data.column.index === 6) {
                //   data.cell.styles.overflow = "linebreak";
                //   data.cell.styles.fontSize = 7;
                // }
              },
            });

            y = doc.lastAutoTable.finalY + 20;
          });

          doc.save(
            `insightsExport_${data.title.replace(
              /\s+/g,
              "_"
            )}_Report_${date.getTime()}.pdf`
          );

          resolve();
        });
      }, 500); // Wait 500ms for DOM to update
    });
  },
};
